// MA-C19 — `numeral_card` renderer for read-the-numeral items (ms.math.00c/
// 00d/00e: "Which number is on the card?", "What number do you see?", "Tap
// the matching number").
//
// THE GAP THIS CLOSES: these items rendered BLANK — there was no numeral
// renderer, so the mathExemplar numeral_card items (60) and five foundation
// representations (numeral_glyph, numeral_with_quantity_choices,
// numeral_identification, numeral_to_quantity_match, drag_numeral_to_quantity
// — 90 more) served a question with nothing to look at.
//
// WHAT IT DRAWS: the authored numeral as a large card-style glyph. Unlike
// DotPatternRenderer (MA-C18), which deliberately omits any `<text>` numeral
// because the COUNT is the hidden answer, here the numeral IS the authored
// stimulus AND the question itself ("which number is THIS") — the same
// "the visual IS the question" class as ClockRenderer / PlaceValueBlocksRenderer
// (read-the-clock / read-the-blocks). Showing it is faithful, not a leak.
//
// ANSWER-SAFE by construction: the mapper (contentToEngineItem.ts) only ever
// emits this kind when the displayed numeral === the item's correct answer —
// this component just draws whatever `total` it is given.
//
// ADDITIVE — registered in rendererRegistry.tsx beside the other built-ins.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";

function safeNumeral(total: unknown): string | null {
  const n = Math.floor(Number(total));
  return Number.isFinite(n) && n >= 0 ? String(n) : null;
}

export default function NumeralCardRenderer({ total }: MathRendererProps): ReactElement {
  const label = safeNumeral(total);
  if (label === null) {
    // Fail-safe: never throw, never draw a misleading/blank numeral.
    return <svg viewBox="0 0 10 10" width="0" height="0" role="img" aria-label="Numeral card" />;
  }
  return (
    <svg viewBox="0 0 160 160" width="100%" style={{ maxWidth: 160 }} role="img"
      aria-label={`Numeral card showing ${label}`}>
      <rect x="4" y="4" width="152" height="152" rx="18"
        fill="var(--pal-cream, #faf3e8)" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="3" />
      <text x="80" y="112" textAnchor="middle" fontSize="96" fontWeight="700"
        fontFamily="inherit" fill="var(--color-text-primary, #3d3228)">{label}</text>
    </svg>
  );
}
